// Single source of truth for per-page SEO metadata.
// Consumed by both the client (SPA navigation, see AppShell) and the
// build-time prerender script (prerender.mjs, via entry-server).

export const SITE = {
  name: 'Redbax',
  // Canonical host. The apex and www both serve this marketing site;
  // canonical tags consolidate ranking signals onto the apex.
  url: 'https://redbax.io',
  ogImage: 'https://redbax.io/og-image.svg',
} as const;

export interface PageMeta {
  title: string;
  description: string;
}

// Keyed by route pathname.
export const routeMeta: Record<string, PageMeta> = {
  '/': {
    title: 'Redbax — AI Data Loss Prevention for ChatGPT & Copilot',
    description:
      'AI endpoint DLP that blocks PII, card data, credentials and source code from leaking into ChatGPT, Copilot, Claude and 40+ AI tools before it leaves the device.',
  },
  '/features': {
    title: 'Features — AI Endpoint DLP & SOC Dashboard | Redbax',
    description:
      'OS-level interception, ML detection of PII, PCI, credentials and source code, policy controls and a real-time SOC dashboard. Detection runs on the device — no cloud proxy, no browser extensions.',
  },
  '/contact': {
    title: 'Register Interest — Redbax AI Endpoint Protection',
    description:
      'Register your interest in Redbax AI endpoint protection. Tell us about your data-protection needs and be first to know when we launch.',
  },
  '/ai-dlp': {
    title: 'AI Data Loss Prevention (DLP): A Complete Guide | Redbax',
    description:
      'What AI DLP is, why traditional DLP misses GenAI, and how to stop PII, secrets and source code leaking into ChatGPT, Copilot and Claude. A practical guide.',
  },
  '/shadow-ai': {
    title: 'Shadow AI: Risks and How to Control It | Redbax',
    description:
      'Shadow AI is employees using unapproved AI tools with company data. Learn the risks, real examples, and how to regain control without banning AI outright.',
  },
  '/stop-chatgpt-data-leaks': {
    title: 'How to Stop Data Leaks to ChatGPT | Redbax',
    description:
      'A practical guide to preventing sensitive data — PII, card numbers, source code and secrets — from leaking into ChatGPT and other AI chatbots.',
  },
  '/ai-dlp-vs-traditional-dlp': {
    title: "AI DLP vs Traditional DLP: What's the Difference? | Redbax",
    description:
      'Traditional DLP was built for email and file transfers, not AI prompts. See where legacy DLP fails on GenAI and what AI-native DLP does differently.',
  },
};

// Routes that should be prerendered to static HTML and listed in the sitemap.
export const seoRoutes: string[] = [
  '/',
  '/features',
  '/contact',
  '/ai-dlp',
  '/shadow-ai',
  '/stop-chatgpt-data-leaks',
  '/ai-dlp-vs-traditional-dlp',
];

// Content cluster: the pillar plus supporting articles. Used for cross-linking
// (related-reading blocks, footer) and for per-page Article/Breadcrumb schema.
export interface ContentEntry {
  path: string;
  label: string;
  breadcrumb: string;
  blurb: string;
  datePublished: string;
}

export const contentPages: ContentEntry[] = [
  {
    path: '/ai-dlp',
    label: 'AI Data Loss Prevention: A Complete Guide',
    breadcrumb: 'AI DLP',
    blurb: 'The what, why and how of preventing data leaks into generative AI tools.',
    datePublished: '2026-07-15',
  },
  {
    path: '/shadow-ai',
    label: 'Shadow AI: Risks and How to Control It',
    breadcrumb: 'Shadow AI',
    blurb: 'Why employees use unapproved AI tools with company data — and what to do about it.',
    datePublished: '2026-07-15',
  },
  {
    path: '/stop-chatgpt-data-leaks',
    label: 'How to Stop Data Leaks to ChatGPT',
    breadcrumb: 'Stop ChatGPT data leaks',
    blurb: 'A practical playbook for keeping sensitive data out of AI chatbots.',
    datePublished: '2026-07-15',
  },
  {
    path: '/ai-dlp-vs-traditional-dlp',
    label: 'AI DLP vs Traditional DLP',
    breadcrumb: 'AI DLP vs traditional DLP',
    blurb: 'Where legacy DLP falls short on AI prompts and what AI-native DLP changes.',
    datePublished: '2026-07-15',
  },
];

export const DEFAULT_META: PageMeta = routeMeta['/'];

export function metaForPath(pathname: string): PageMeta {
  return routeMeta[pathname] ?? DEFAULT_META;
}

export function canonicalFor(pathname: string): string {
  return pathname === '/' ? `${SITE.url}/` : `${SITE.url}${pathname}`;
}

export function contentEntryFor(pathname: string): ContentEntry | undefined {
  return contentPages.find((c) => c.path === pathname);
}

// Per-page structured data (Article + breadcrumb) for content pages.
// Injected into the prerendered HTML by prerender.mjs. Returns null for
// non-content routes (they rely on the site-wide JSON-LD in index.html).
export function pageJsonLd(pathname: string): object | null {
  const entry = contentEntryFor(pathname);
  if (!entry) return null;
  const meta = metaForPath(pathname);
  const url = canonicalFor(pathname);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: meta.title.replace(/ \| Redbax$/, ''),
        description: meta.description,
        inLanguage: 'en',
        author: { '@id': `${SITE.url}/#organization` },
        publisher: { '@id': `${SITE.url}/#organization` },
        datePublished: entry.datePublished,
        dateModified: entry.datePublished,
        mainEntityOfPage: url,
        url,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE.url}/` },
          { '@type': 'ListItem', position: 2, name: entry.breadcrumb, item: url },
        ],
      },
    ],
  };
}
